import {
  useEffect,
  useState,
  useMemo,
  memo
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Card,
  Divider,
  alpha,
  IconButton,
  Tooltip,
  Stack,
  Button
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router';
import { getStateName } from 'src/utils/getInitials';
import { useDispatch, useSelector } from 'react-redux';
import { getSiteList } from 'src/redux/siteSlice';
import Loader from 'src/common/Loader';
import ROUTE from 'src/utils/routeConstants';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, siteTableHeaders } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

// --- Modern Status Pill ---
const StatusPill = ({ active }) => (
  <Chip
    label={active ? 'Active' : 'Inactive'}
    color={active ? 'success' : 'error'}
    size="small"
    variant="outlined"
    sx={{ fontWeight: 600 }}
  />
);

StatusPill.propTypes = {
  active: PropTypes.bool
};

// --- Mobile Card View ---
const MobileCardView = memo(({ site, onViewClick }) => (
  <Card
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      boxShadow: 'none',
      border: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Stack direction="row" justifyContent="space-between" mb={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={400}>
          {site.code || 'N/A'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {
            `#${site.id}`
          }
        </Typography>
      </Box>
      <StatusPill active={site.active} />
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <LocationIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
      <Typography variant="body2" color="text.secondary" fontWeight={400}>
        {site.city && site.state
          ? `${site.city}, ${getStateName(site.state)}`
          : 'Location not set'}
      </Typography>
    </Stack>
    <Button
      fullWidth
      size="small"
      variant="outlined"
      onClick={() => onViewClick(site.id)}
    >
      View Details
    </Button>
  </Card>
));

MobileCardView.displayName = 'MobileCardView';

MobileCardView.propTypes = {
  site: PropTypes.object.isRequired,
  onViewClick: PropTypes.func.isRequired
};

const SiteListTable = ({ refreshData, searchText, ...rest }) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const { loading, siteList, totalCount } = useSelector((state) => state.sites);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('name');
  const siteAlignById = useMemo(() => getAlignByColumnId(siteTableHeaders), []);

  useEffect(() => {
    setPage(0);
    dispatch(getSiteList({ limit, pageNo: 1, params: { name: searchText } }));
  }, [dispatch, limit, refreshData, searchText]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getSiteList({ limit, pageNo: newPage + 1, params: { name: searchText } }));
  };

  const viewSiteInfo = (siteId) => {
    history.push(`${ROUTE.sites}/${siteId}`);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayData = siteList
    ?.slice()
    .sort(getComparator(order, orderBy)) || [];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Box sx={{ height: '100%' }}>
          {isMobile ? (
            <Box sx={{ width: '100%', height: '100%' }}>
              {displayData.map((site) => (
                <MobileCardView
                  key={site.id}
                  site={site}
                  onViewClick={viewSiteInfo}
                />
              ))}
              <CustomPagination
                page={page}
                rowsPerPage={limit}
                count={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
              />
            </Box>
          ) : (
            // --- DESKTOP VIEW ---
            <Box
              sx={{
                ...rest?.sx,
                width: '100%',
                border: 'none',
                borderRadius: 2,
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflow: 'auto',
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: '#F9FAFB',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    zIndex: 2
                  },
                  '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
                    position: 'sticky',
                    right: 0,
                    zIndex: 3,
                    backgroundColor: '#F9FAFB',
                    boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ minWidth: isTablet ? 800 : 1050 }}>
                  <Table stickyHeader>
                    {/* Pass explicit 'sx' to fix header color.
                      Now EnhancedTableHead will use this background instead of the gradient.
                    */}
                    <EnhancedTableHead
                      order={order}
                      orderBy={orderBy}
                      headCells={siteTableHeaders}
                      onRequestSort={handleRequestSort}
                      sx={{
                        backgroundColor: '#F9FAFB', // Light Gray Background
                        borderBottom: `1px solid ${theme.palette.divider}`
                      }}
                    />
                    <TableBody>
                      {displayData.length > 0 ? (
                        displayData.map((site) => (
                          <TableRow
                            hover
                            key={site.id}
                            sx={{
                              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': {
                                backgroundColor: '#F9FAFB' // Subtle hover
                              }
                            }}
                          >
                            <TableCell align={siteAlignById.code}>
                              <Typography variant="body2" color="text.secondary">
                                {site.code}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.name}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                color="text.primary"
                              >
                                {site.name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.city}>
                              <Typography variant="body2" color="text.secondary">
                                {site.city && site.state
                                  ? `${site.city}, ${getStateName(site.state)}`
                                  : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.dispensers_count}>
                              <Typography fontWeight={400} color="text.primary">
                                {site.dispensers_count || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.tanks_count}>
                              <Typography fontWeight={400} color="text.primary">
                                {site.tanks_count || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.vehicle_count}>
                              <Typography fontWeight={400} color="text.primary">
                                {site.vehicle_count || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.operators_count}>
                              <Typography fontWeight={400} color="text.primary">
                                {site.operators_count || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align={siteAlignById.active}>
                              <StatusPill active={site.active} />
                            </TableCell>
                            <TableCell
                              align={siteAlignById.action}
                              sx={{
                                position: 'sticky',
                                right: 0,
                                zIndex: 1,
                                backgroundColor: '#FFFFFF',
                                boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                              }}
                            >
                              <Tooltip title="View Details">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewSiteInfo(site.id);
                                  }}
                                  size="medium"
                                  aria-label="View details"
                                  sx={getTableActionIconButtonSx(theme)}
                                >
                                  <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                            <Stack alignItems="center" spacing={1}>
                              <BusinessIcon sx={{ fontSize: 40, color: 'text.disabled', opacity: 0.5 }} />
                              <Typography color="text.secondary">
                                No Sites Found
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
              <Divider sx={{ flexShrink: 0 }} />
              <Box
                sx={{
                  p: 1,
                  bgcolor: '#FFFFFF',
                  flexShrink: 0
                }}
              >
                <CustomPagination
                  page={page}
                  rowsPerPage={limit}
                  count={totalCount}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleLimitChange}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

SiteListTable.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string
};

export default SiteListTable;
